import type { Organization, Workspaces } from '@/modules/shared/types'

export type OrgAccess = 'member' | 'store-only'

export type OrgAccessResolution =
  | { kind: 'member'; org: Organization }
  | { kind: 'store-only'; org: Organization; redirectToClientSlug: string | null }
  | { kind: 'not-found' }

/** Segments under /$orgSlug that are agency-level (not a client slug). */
const ORG_LEVEL_SEGMENTS = new Set(['overview', 'prospects', 'setting-up', 'onboarding'])

/**
 * True when `pathname` points at an agency-level page of `orgSlug` (or the
 * bare org root). Store-only users are redirected off these to their store.
 */
export function isOrgLevelPath(pathname: string, orgSlug: string): boolean {
  const prefix = `/${orgSlug}`
  if (!pathname.startsWith(prefix)) return false
  const rest = pathname.slice(prefix.length).replace(/^\//, '')
  const segment = rest.split('/')[0] ?? ''
  return segment === '' || ORG_LEVEL_SEGMENTS.has(segment)
}

/**
 * Pure decision tree for `/$orgSlug` access: agency membership grants full
 * ('member') access; a direct store membership in that agency grants
 * restricted ('store-only') access, scoped to that store and never to
 * agency-level pages; neither grants 'not-found'.
 *
 * Self-serve gating is a separate, stateful concern (see
 * `shouldGateSelfServe`) and intentionally stays out of this classifier.
 */
export function resolveOrgAccess(
  ws: Workspaces,
  orgSlug: string,
  pathname: string,
): OrgAccessResolution {
  const org = ws.organizations.find((o) => o.slug === orgSlug)
  if (org) return { kind: 'member', org }

  const myStores = ws.directStores.filter((s) => s.orgSlug === orgSlug)
  const firstStore = myStores[0]
  if (!firstStore) return { kind: 'not-found' }

  const restrictedOrg: Organization = {
    id: firstStore.orgId,
    slug: firstStore.orgSlug,
    name: firstStore.orgName,
  }

  return {
    kind: 'store-only',
    org: restrictedOrg,
    redirectToClientSlug: isOrgLevelPath(pathname, orgSlug) ? firstStore.slug : null,
  }
}
